import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowBackIosNew } from '@mui/icons-material';
import { Link, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useGatewayListing } from 'api/apiHooks';
import AuthHeader from 'components/auth/AuthHeader';
import PortalListingOnboarding from 'containers/onboarding/PortalListingOnboarding';
import { motion, useSpring } from 'framer-motion';
import useAuth from 'hooks/useAuth';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useNavigate,
  Link as RouterLink,
  useSearchParams,
} from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { PATH_AUTH, ROOT_NAVIGATION } from 'routes/paths';
import { getRandomId } from 'utils';
import axiosInstance from 'utils/axios';
import { setSession } from 'utils/jwt';
import * as Yup from 'yup';
import ChooseTemplate from './ChooseTemplate';
import EmailScreen from './EmailScreen';
import SelectGatewayScreen from './SelectGatewayScreen';

const AnimationContainer = styled('div')`
  &.display-appear {
    opacity: 0;
  }

  &.display-enter {
    opacity: 0;
    transition: all 1s ease;
    transform: translateX(-30px);
  }

  &.display-appear-active {
    opacity: 1;
    transition: all 1s ease;
    transform: translateX(-30px);
  }

  &.display-enter-active {
    opacity: 1;
    transition: all 1s ease;
  }

  &.display-enter-done,
  &.display-appear-done {
    opacity: 1;
    transition: all 1s ease;
    transform: translateX(-30px);
  }

  &.display-exit {
    opacity: 0;
    transform: translateX(-30px);
    transition: all 1s ease;
  }

  &.display-exit-active {
    transition: all 1s ease;
  }
`;

type FormValuesProps = {
  email: string;
  apiError: string;
};

const Onboarding = () => {
  const [step, setStep] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { dispatch, updateUser } = useAuth();
  const [userToken, setUserToken] = useState<string | null>(null);
  const { t } = useTranslation();
  const [stripeKeyError, setStripeKeyError] = useState('');
  const [searchParams] = useSearchParams();
  const [payPalKeyError, setPayPalKeyError] = useState('');
  const userSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('email_format_incorrect'))
      .required(t('email_is_required')),
  });

  const scaleX = useSpring(step * 0.25, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });
  const success = searchParams.get('success');
  useEffect(() => {
    window.localStorage.removeItem('setStripe');
    if (window.location.href.indexOf('success') > -1) {
      if (success === 'false') {
        window.localStorage.setItem('setStripe', 'true');
      }
    }
  }, [success]);
  useEffect(() => {
    if (window.localStorage.getItem('setStripe') === 'true') {
      setStep(3);
      enqueueSnackbar('Could not connect', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    scaleX.set(step * 0.25);
  }, [step, scaleX]);

  const theme = useTheme();

  const defaultValues = {
    email: '',
  };
  const [submitting, setSubmitting] = useState(false);
  const [portalConnecting, setPortalConnecting] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [portalAlreadyConnected, setPortalAlreadyConnected] = useState<
    boolean | null
  >(null);
  const refetchRef = useRef<{ refetch: VoidFunction }>();
  const [portalId, setPortalId] = useState<null | number>(null);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const [formCreationInProgress, setFormCreationInProgress] = useState(true);
  const [creatingForm, setCreatingForm] = useState(false);
  const gatewayConfig = useState<string | null>(null);
  const [userData, setUserData] = useState<any>();
  const [methodSelected, setMethodSelected] = useState('stripe');

  const { refetch: refetchGatewayListingData } = useGatewayListing({
    enabled: false,
  });

  const methods = useForm<FormValuesProps>({
    mode: 'all',
    resolver: yupResolver(userSchema),
    defaultValues,
  });

  const {
    setError,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = methods;

  const makeUser = async (data: FormValuesProps) => {
    setSubmitting(true);
    try {
      const result = await axiosInstance.post<{
        success: boolean;
        message: string;
        user_data: any;
      }>('/formpayhome/onboarding/onBoardingUserConnect', {
        email: data.email,
      });

      if (result.data.success === false) {
      } else {
        setUserData(result.data.user_data.data);
        setStep(step + 1);
      }
    } catch (err: any) {
      if (err.code === 'user_onBoarded') {
        setError('email', { message: t('user_onboarded') });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const connectToPortal = async (data: FormValuesProps) => {
    setPortalConnecting(true);
    try {
      const result = await axiosInstance.post<{
        success: boolean;
        message: string;
        url: string;
      }>('/formpayhome/onboarding/onBoardingPortalConnect', {
        email: watch('email'),
        custom_url: `${window.location.protocol}//${window.location.host}/onboarding/callback`,
      });

      if (result.data.success) {
        setRedirectUrl(
          result.data.url +
            `&email=${watch('email')}&state-front=${getRandomId(
              10
            )}&custom_url=${window.location.protocol}//${
              window.location.host
            }/onboarding/callback`
        );
      } else {
        setStep(step + 1);
      }
    } catch (err) {
    } finally {
      setPortalConnecting(false);
    }
  };

  const onCode = React.useCallback(
    (code: string, params: any) => {
      if (code === 'error') {
        setPortalAlreadyConnected(true);
      } else {
        setPortalId(params.get('portal_id'));
        window.sessionStorage.setItem('portal_id', params.get('portal_id'));
        dispatch({
          type: 'LOGIN_ONBOARDED_USER',
          payload: {
            user: userData,
          },
        });
        setSession(params.get('user_token'));
        setUserToken(params.get('user_token'));
        window.sessionStorage.setItem('user_token', params.get('user_token'));
        refetchGatewayListingData();
        setStep(step + 1);
      }
    },
    [dispatch, refetchGatewayListingData, step, userData]
  );

  const createFormInHubspot = async () => {
    setCreatingForm(true);
    setSession(userToken);
    try {
      const result = await axiosInstance.post(
        'formpayhome/onboarding/hubFormCreation',
        {
          portal_id: portalId,
        }
      );
      const formId = result.data.hubspot_form_id;
      setSelectedFormId(formId);
      refetchRef.current?.refetch();
    } finally {
      setCreatingForm(false);
      setFormCreationInProgress(false);
    }
  };

  const [loadingUserSubmit, setLoadingUserSubmit] = useState(false);
  const [loadingOurStripeKeys, setLoadingOurStripeKeys] = useState(false);

  const handlePaymentSubmit = async (data: any) => {
    setLoadingUserSubmit(true);
    try {
      const result = await axiosInstance.post(
        '/formpayhome/onboarding/setupKeys',
        {
          ...data,
          gateway: methodSelected,
          keys_type: 'user',
          portal_id: portalId,
          theme: 'stripe',
        }
      );
      if (result.data.success) {
        const result = await axiosInstance.post(
          '/formpayhome/onboarding/portalFormCreation',
          {
            portal_id: portalId,
            gateway: methodSelected,
            selectedFormId,
          }
        );
        dispatch({
          type: 'SET_ISAUTHENTICATED',
          payload: {
            user: userData,
          },
        });
        if (result.data.success) {
          updateUser();
          navigate(
            ROOT_NAVIGATION.general.formEdit(
              portalId as unknown as string,
              result.data.portal_form_id
            )
          );
        }
      }
    } catch (err: any) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      if (methodSelected === 'stripe') {
        setStripeKeyError(err);
        enqueueSnackbar(err?.message, { variant: 'error' });
      } else {
        setPayPalKeyError(err);
        enqueueSnackbar(err?.message, { variant: 'error' });
      }
    } finally {
      setLoadingUserSubmit(false);
    }
  };

  const handleOurPaymentKeys = async () => {
    setLoadingOurStripeKeys(true);
    try {
      const result = await axiosInstance.post(
        '/formpayhome/onboarding/setupKeys',
        {
          keys_type: 'own',
          gateway: methodSelected,
          portal_id: portalId,
          theme: 'stripe',
        }
      );
      if (result.data.success) {
        const result = await axiosInstance.post(
          '/formpayhome/onboarding/portalFormCreation',
          {
            portal_id: portalId,
            gateway: methodSelected,
            selectedFormId,
          }
        );
        dispatch({
          type: 'SET_ISAUTHENTICATED',
          payload: {
            user: userData,
          },
        });

        updateUser();
        navigate(
          ROOT_NAVIGATION.general.formEdit(
            portalId as unknown as string,
            result.data.portal_form_id
          )
        );
      }
    } catch (err: any) {
    } finally {
      setLoadingOurStripeKeys(false);
    }
  };

  return (
    <>
      <motion.div
        style={{
          scaleX: scaleX,
          minHeight: '8px',
          position: 'fixed',
          backgroundColor: `${theme.palette.primary.main}`,
          top: 0,
          left: 0,
          right: 0,
          transformOrigin: '0%',
        }}
      ></motion.div>
      <AuthHeader>
        <>
          <Typography variant='body2' sx={{ mr: { md: 2 } }}>
            <Link
              variant='subtitle2'
              component={RouterLink}
              to={PATH_AUTH.login}
            >
              {t('login')}
            </Link>
            &nbsp;|&nbsp;
            <Link
              variant='subtitle2'
              component={RouterLink}
              to={PATH_AUTH.register}
            >
              {t('register')}
            </Link>
          </Typography>
        </>
      </AuthHeader>
      <div className='container mx-auto w-full h-full'>
        <div className='mt-[180px]'>
          {step !== 0 && (
            <span
              className='text-sm ml-[8rem] text-primary-main cursor-pointer hover:text-primary-darker flex items-center'
              onClick={() => {
                setRedirectUrl(null);
                setSession(null);
                setStep(step - 1);
              }}
            >
              {<ArrowBackIosNew className='max-w-[1rem] max-h-[1rem]' />}{' '}
              {t('back')}
            </span>
          )}
          {step === 0 && (
            <CSSTransition
              in={step === 0}
              timeout={1000}
              classNames='display'
              appear
            >
              <AnimationContainer className='flex justify-center items-center ml-10 h-full'>
                <EmailScreen
                  makeUser={handleSubmit(makeUser)}
                  methods={methods}
                  submitting={submitting}
                />
              </AnimationContainer>
            </CSSTransition>
          )}

          {step === 1 && (
            <CSSTransition
              in={step === 1}
              timeout={1000}
              classNames='display'
              unmountOnExit
              appear
            >
              <AnimationContainer className='flex flex-col justify-center items-center h-full'>
                <PortalListingOnboarding
                  connectToPortal={connectToPortal as unknown as () => void}
                  portalConnecting={portalConnecting}
                  redirectUrl={redirectUrl}
                  onCode={onCode}
                  portalAlreadyConnected={portalAlreadyConnected}
                  userToken={userToken}
                  setUserToken={setUserToken}
                  nextStep={() => setStep(step + 1)}
                />
              </AnimationContainer>
            </CSSTransition>
          )}

          {step === 2 && (
            <CSSTransition
              in={step === 2}
              timeout={1000}
              classNames='display'
              unmountOnExit
              appear
            >
              <AnimationContainer className='flex justify-center items-center'>
                <ChooseTemplate
                  selectedFormId={selectedFormId}
                  setSelectedFormId={setSelectedFormId}
                  formCreationInProgress={formCreationInProgress}
                  createFormInHubspot={createFormInHubspot}
                  creatingForm={creatingForm}
                  nextStep={() => setStep(step + 1)}
                  ref={refetchRef}
                  userToken={userToken}
                  setUserToken={setUserToken}
                />
              </AnimationContainer>
            </CSSTransition>
          )}

          {step === 3 && (
            <CSSTransition
              in={step === 3}
              timeout={1000}
              classNames='display'
              unmountOnExit
              appear
            >
              <AnimationContainer className='block mx-auto z-20'>
                <SelectGatewayScreen
                  gatewayConfig={gatewayConfig}
                  loadingUserSubmit={loadingUserSubmit}
                  loadingOurPaymentKeys={loadingOurStripeKeys}
                  onSubmit={handlePaymentSubmit}
                  handleOurPaymentKeys={handleOurPaymentKeys}
                  methodSelected={methodSelected}
                  setMethodSelected={setMethodSelected}
                  stripeKeyError={stripeKeyError}
                  payPalKeyError={payPalKeyError}
                  userToken={userToken}
                  setUserToken={setUserToken}
                />
              </AnimationContainer>
            </CSSTransition>
          )}
        </div>
      </div>
    </>
  );
};

export default Onboarding;
